/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useCallback, useState } from 'react'

interface ShowDialogProps {
  title: string
  description?: string
  showConfirmation?: boolean
  confirmButtonText?: string
  cancelButtonText?: string
  content?: JSX.Element
  action?: () => unknown | Promise<unknown>
  cancelAction?: () => unknown | Promise<unknown>
  type?: 'success' | 'error' | 'warning' | 'info'
}

type DialogBoxContextDataProps = {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  isLoadingDialogAction: boolean
  setIsLoadingDialogAction: (value: boolean) => void
  dialogData: ShowDialogProps
  showDialog: (dialogData: ShowDialogProps) => void
}

type DialogBoxProviderProps = {
  children: ReactNode
}

export const DialogBoxContext = createContext<DialogBoxContextDataProps>(
  {} as DialogBoxContextDataProps,
)

const INITIAL_DATA: ShowDialogProps = {
  title: '',
  showConfirmation: true,
}

export function DialogBoxProvider({ children }: DialogBoxProviderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoadingDialogAction, setIsLoadingDialogAction] = useState(false)

  const [dialogData, setDialogData] = useState<ShowDialogProps>(INITIAL_DATA)

  const showDialog = useCallback(({
    title,
    action,
    cancelAction,
    cancelButtonText,
    confirmButtonText,
    content,
    description,
    type = 'error',
    showConfirmation = false,
  }: ShowDialogProps) => {
    setDialogData({
      title,
      action,
      cancelAction,
      cancelButtonText,
      confirmButtonText,
      content,
      description,
      type,
      showConfirmation,
    })
    setIsOpen(true)
  }, [])

  function handleSetIsOpen(value: boolean) {
    setIsOpen(value)
  }

  function handleSetIsLoadingDialogAction(value: boolean) {
    setIsLoadingDialogAction(value)
  }

  return (
    <DialogBoxContext.Provider
      value={{
        isOpen,
        setIsOpen: handleSetIsOpen,
        isLoadingDialogAction,
        setIsLoadingDialogAction: handleSetIsLoadingDialogAction,
        dialogData,
        showDialog,
      }}
    >
      {children}
    </DialogBoxContext.Provider>
  )
}
